import { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  Chip,
  Collapse,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { statusColors } from '../theme'
import { formatFullDate, getDaySchedule, getStandardHours } from '../utils'
import { allocateSchedule } from 'src/actions/timesheet.action'
import { getTaskdropdownData } from 'src/actions/project-management.action'
import { successSnackBar, warningSnackBar } from 'src/actions/common.action'
import { getUserWorkHourAssignment, getWorkHourSetting } from 'src/actions/work-hour-settings.action'
import { createChainedAllocationTimes, getAssignmentSetting, getAssignmentSettingId } from '../work-hour-allocation'
import { filterLockedSections } from 'src/util/section-dropdown-format'
import { convertToReactSelectFormat } from 'src/util/section-dropdown-format'
import { TIMESHEET_TASK_DROPDOWN_PARAMS } from '../../task-dropdown-options'
import CustomInput from 'src/components/inputs/new-input'
import SelectInput from 'src/components/inputs/select'
import MultiSelect from 'src/components/inputs/multi-select'
import { roundValue, toDecimal } from 'src/util/decimal-utils'

const WH_WEEKDAY_NAMES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']

function hasValidGlobalTotalHrs(letterhead) {
  const parsed = parseFloat(letterhead?.total_hrs)
  return Number.isFinite(parsed) && parsed > 0
}

export function getDailyHoursFromSetting(setting) {
  const rules = setting?.weekday_rules
  if (!rules) return null
  let totalMinutes = 0
  let daysWithRules = 0
  WH_WEEKDAY_NAMES.forEach((day) => {
    const dayRules = rules[day]
    if (!Array.isArray(dayRules) || dayRules.length === 0) return
    let dayMinutes = 0
    let hasStandard = false
    dayRules.forEach((rule) => {
      if (rule.time_type !== 'STANDARD') return
      hasStandard = true
      // Hourly-mode bands carry an Hours value directly, with no start/end to compute from
      if (Number.isFinite(rule.hours)) {
        dayMinutes += rule.hours * 60
        return
      }
      const [sh, sm] = String(rule.start || '').split(':').map(Number)
      const [eh, em] = String(rule.end || '').split(':').map(Number)
      if (!Number.isFinite(sh) || !Number.isFinite(eh)) return
      let startMins = sh * 60 + (sm || 0)
      let endMins = eh * 60 + (em || 0)
      if (endMins <= startMins) endMins += 24 * 60
      dayMinutes += endMins - startMins
    })
    if (!hasStandard) return
    daysWithRules++
    totalMinutes += dayMinutes
  })
  if (daysWithRules === 0) return null
  return Math.round((totalMinutes / daysWithRules / 60) * 10) / 10
}


export default function ConfirmAllocationsDialog({
  open,
  onClose,
  staff,
  selectionByStaff,
  scheduleMap,
  availability,
  projects,
  onConfirm,
}) {
  const dispatch = useDispatch()
  const globalSetting = useSelector((state) => state.globalSetting)

  const standardHours = getStandardHours(globalSetting?.letterhead)

  const [projectId, setProjectId] = useState('')
  const [selectedTask, setSelectedTask] = useState(null)
  const [taskOptions, setTaskOptions] = useState([])
  const [tasksLoading, setTasksLoading] = useState(false)
  const [expanded, setExpanded] = useState(() => new Set())
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [dailyHoursByStaff, setDailyHoursByStaff] = useState(() => new Map())

  const updateDailyHours = useCallback((staffId, value) => {
    setDailyHoursByStaff((prev) => {
      const next = new Map(prev)
      next.set(staffId, value)
      return next
    })
  }, [])

  const parseDailyHours = useCallback((staffId) => {
    const raw = dailyHoursByStaff.get(staffId)
    const parsed = parseFloat(raw)
    if (!Number.isFinite(parsed) || parsed < 0) return standardHours
    return Math.round(parsed * 10) / 10
  }, [dailyHoursByStaff, standardHours])

  // Reset state each time dialog opens
  useEffect(() => {
    if (open) {
      setProjectId('')
      setSelectedTask(null)
      setTaskOptions([])
      setExpanded(new Set())
      setDone(false)
      // Initialise every selected staff member's daily hours from the global setting.
      // Fall back to the staff's Work-Hour Setting only when the global value is missing.
      const hasGlobalTotalHrs = hasValidGlobalTotalHrs(globalSetting?.letterhead)
      const selectedStaffIds = staff
        .filter((s) => (selectionByStaff.get(s.id)?.length ?? 0) > 0)
        .map((s) => s.id)
      const initial = new Map()
      selectedStaffIds.forEach((id) => initial.set(id, standardHours))
      setDailyHoursByStaff(initial)

      if (!hasGlobalTotalHrs) {
        const fetchDailyHours = async () => {
          const updated = new Map(initial)
          await Promise.all(selectedStaffIds.map(async (staffId) => {
            try {
              const assignmentResponse = await dispatch(getUserWorkHourAssignment(staffId))
              const assignmentSetting = getAssignmentSetting(assignmentResponse)
              const settingId = getAssignmentSettingId(assignmentResponse)
              const setting = assignmentSetting || (settingId ? await dispatch(getWorkHourSetting(settingId)) : null)
              const resolved = setting?.data || setting
              const hrs = getDailyHoursFromSetting(resolved)
              if (hrs != null) updated.set(staffId, hrs)
            } catch (e) {
              // Keep account standard on failure
            }
          }))
          setDailyHoursByStaff(updated)
        }
        fetchDailyHours()
      }
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load tasks when project changes
  useEffect(() => {
    if (!projectId) { setTaskOptions([]); setSelectedTask(null); return }
    setTasksLoading(true)
    setSelectedTask(null)
    dispatch(getTaskdropdownData(projectId, TIMESHEET_TASK_DROPDOWN_PARAMS))
      .then((response) => {
        const filtered = filterLockedSections(response || [], [])
        const formatted = convertToReactSelectFormat(filtered, null, true)
        setTaskOptions(formatted)
      })
      .catch(() => setTaskOptions([]))
      .finally(() => setTasksLoading(false))
  }, [projectId, dispatch])

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === projectId) || null,
    [projects, projectId],
  )

  const rows = useMemo(() => {
    return staff
      .filter((s) => (selectionByStaff.get(s.id)?.length ?? 0) > 0)
      .map((s) => {
        const keys = [...(selectionByStaff.get(s.id) ?? [])].sort()
        const conflictInstances = keys
          .map((k) => ({ key: k, ...getDaySchedule(scheduleMap, s.id, k, availability, standardHours) }))
          .filter((d) => d.bookedHours > 0 || d.status === 'off')
          .map((d) => ({
            key: d.key,
            type: d.status === 'off' ? 'unavailable' : 'booking',
            status: d.status,
            project: d.project || 'Existing booking',
            bookedHours: d.bookedHours,
            capacityHours: d.capacityHours,
            fullDay: d.status === 'full',
          }))
        const userDailyHours = parseDailyHours(s.id)
        const rawDailyHours = dailyHoursByStaff.get(s.id) ?? standardHours
        const hours = keys.length * userDailyHours
        return {
          staff: s,
          days: keys.length,
          dailyHours: rawDailyHours,
          hours,
          cost: hours * (s.hourlyRate || 0), // calculated, display hidden in v1
          conflicts: conflictInstances.length,
          conflictInstances,
        }
      })
  }, [staff, selectionByStaff, scheduleMap, availability, standardHours, dailyHoursByStaff, parseDailyHours])

  const totals = useMemo(
    () => ({
      days: rows.reduce((a, r) => a + r.days, 0),
      hours: rows.reduce((a, r) => a + r.hours, 0),
      cost: rows.reduce((a, r) => a + r.cost, 0), // hidden in v1
      conflicts: rows.reduce((a, r) => a + r.conflicts, 0),
    }),
    [rows],
  )

  function toggle(id) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function handleConfirm() {
    if (!selectedProject) return
    setSubmitting(true)
    try {
      const staffById = new Map(staff.map((s) => [s.id, s]))
      const users = await Promise.all([...selectionByStaff.entries()].map(async ([staffId, days]) => {
        const s = staffById.get(staffId)
        if (!s || !days.length) return null

        const assignmentResponse = await dispatch(getUserWorkHourAssignment(staffId))
        const assignmentSetting = getAssignmentSetting(assignmentResponse)
        const settingId = getAssignmentSettingId(assignmentResponse)
        const setting = assignmentSetting || (settingId ? await dispatch(getWorkHourSetting(settingId)) : null)
        const resolvedSetting = setting?.data || setting
        if (!resolvedSetting) throw new Error(`${s.name} has no active work-hour setting.`)
        const userDailyHours = parseDailyHours(staffId)
        const allocations = days.map((day) => {
          const latestEndTime = scheduleMap.get(`${staffId}:${day}`)?.latestEndTime || null
          const times = createChainedAllocationTimes(resolvedSetting, day, userDailyHours, latestEndTime, globalSetting?.letterhead?.start_time)
          if (!times) throw new Error(`${s.name} has no configured work-hour period for ${day}.`)
          return {
            id: '',
            projectId: selectedProject.id,
            projectType: selectedProject.type || 'PROJECT',
            stageId: selectedTask?.isStage ? (selectedTask.value || '') : '',
            taskId: selectedTask && !selectedTask.isStage ? (selectedTask.value || '') : '',
            taskType: selectedTask?.taskType || (selectedTask && !selectedTask.isStage ? 'TASK' : ''),
            taskName: selectedTask?.displayName || selectedTask?.label || '',
            startTime: times.startTime,
            endTime: times.endTime,
            allDayWithBreaks: true,
            hourlyRate: s.hourlyRate || 0,
            previousType: '',
          }
        })

        return { id: staffId, leaves: [], allocations }
      }))

      if (users.some((user) => !user)) throw new Error('Unable to resolve selected users for scheduling.')
      await dispatch(allocateSchedule({ users }))
      dispatch(successSnackBar('Allocations created successfully'))
      setDone(true)
      setTimeout(() => {
        onConfirm()
        setDone(false)
      }, 800)
    } catch (e) {
      dispatch(warningSnackBar(e?.message || 'Unable to create allocations from the selected work-hour settings.'))
      console.error('[ConfirmAllocations] submit error:', e)
    } finally {
      setSubmitting(false)
    }
  }

  const canSubmit = !!projectId && !submitting && !done

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="scheduling-dialog">
      <DialogTitle>
        <Grid container alignItems="center">
          <Grid item xs>
            <span className="dialog-header-title">Confirm Allocations</span>
            <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
              {rows.length} {rows.length === 1 ? 'person' : 'people'} &middot; {totals.days} day-slots &middot; {totals.hours}h
            </div>
          </Grid>
          <Grid item>
            <IconButton onClick={onClose} aria-label="Close">
              <CloseIcon sx={{ color: '#d0021b' }} />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent dividers>
        {done ? (
          <div className="d-flex flex-column align-items-center justify-content-center gap-2 py-5">
            <CheckCircleIcon sx={{ fontSize: 48, color: '#2e9e5b' }} />
            <span style={{ fontSize: 16, fontWeight: 500 }}>Allocations created</span>
          </div>
        ) : (
          <>
            {totals.conflicts > 0 && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                {totals.conflicts} of the selected days have a conflict — an existing booking, or a day the
                person isn't scheduled to work. You can still confirm; conflicting allocations will be created as selected.
              </Alert>
            )}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Resource</TableCell>
                  <TableCell align="right">Days</TableCell>
                  <TableCell align="right">Daily Hrs</TableCell>
                  <TableCell align="right">Hours</TableCell>
                  {/* Cost column hidden in v1 — kept for future display */}
                  <TableCell align="right" style={{ display: 'none' }}>Cost</TableCell>
                  <TableCell align="right">Conflicts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const isOpen = expanded.has(r.staff.id)
                  return [
                    <TableRow key={r.staff.id}>
                      <TableCell>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{r.staff.name}</div>
                        <div style={{ fontSize: 11, color: '#666' }}>{r.staff.role}</div>
                      </TableCell>
                      <TableCell align="right">{r.days}</TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>
                        {(() => {
                          const raw = r.dailyHours
                          const isEmpty = raw === '' || raw === null || raw === undefined
                          const parsed = parseFloat(raw)
                          const isInvalid = isEmpty || !Number.isFinite(parsed) || parsed < 0
                          return (
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <CustomInput
                                name={`dailyHrs-${r.staff.id}`}
                                type="number"
                                value={r.dailyHours}
                                onChange={(e) => updateDailyHours(r.staff.id, e.target.value)}
                                className="mb-2"
                                customInputClass="text-start"
                                step={0.5}
                                min={0}
                                max={24}
                                style={{
                                  width: 50,
                                  ...(isInvalid ? { borderColor: '#d32f2f', boxShadow: '0 0 0 1px #d32f2f' } : {}),
                                }}
                              />
                            </div>
                          )
                        })()}
                      </TableCell>
                      <TableCell align="right">{r.hours}</TableCell>
                      {/* Cost cell hidden in v1 */}
                      <TableCell align="right" style={{ display: 'none' }}>${r.cost.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {r.conflicts > 0 ? (
                          <Chip
                            size="small"
                            color="warning"
                            onClick={() => toggle(r.staff.id)}
                            icon={<WarningAmberIcon />}
                            deleteIcon={
                              <KeyboardArrowDownIcon
                                sx={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
                              />
                            }
                            onDelete={() => toggle(r.staff.id)}
                            label={r.conflicts}
                            aria-expanded={isOpen}
                          />
                        ) : (
                          <Chip size="small" color="success" variant="outlined" label="0" />
                        )}
                      </TableCell>
                    </TableRow>,

                    <TableRow key={`${r.staff.id}-detail`}>
                      <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                        <Collapse in={isOpen} unmountOnExit>
                          <div className="py-2">
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#666' }}>
                              Conflicts ({r.conflicts})
                            </div>
                            <div className="d-flex flex-column gap-2 mt-2">
                              {r.conflictInstances.map((c) => (
                                <div key={c.key} className="conflict-instance d-flex flex-wrap align-items-center justify-content-between gap-2">
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      className="conflict-dot"
                                      style={{
                                        background: c.type === 'unavailable' ? statusColors.off.dot
                                          : c.status === 'over' ? statusColors.over.dot
                                            : c.fullDay ? statusColors.full.dot
                                              : statusColors.partial.dot,
                                      }}
                                    />
                                    <span style={{ fontWeight: 600 }}>{formatFullDate(c.key)}</span>
                                  </div>
                                  {c.type === 'unavailable' ? (
                                    <Chip size="small" label="Not a working day for this person" />
                                  ) : (
                                    <div className="d-flex align-items-center gap-2" style={{ color: '#666' }}>
                                      <span style={{ color: '#333' }}>{c.project}</span>
                                      <span className="d-flex align-items-center gap-1">
                                        <AccessTimeIcon style={{ fontSize: 14 }} />
                                        {c.bookedHours}h / {c.capacityHours}h
                                      </span>
                                      <Chip
                                        size="small"
                                        color={c.status === 'over' ? 'error' : c.fullDay ? 'primary' : 'warning'}
                                        label={
                                          c.status === 'over'
                                            ? `${roundValue(toDecimal(c.bookedHours).minus(toDecimal(c.capacityHours)), 2)}h overbooked`
                                            : c.fullDay
                                              ? 'Booked'
                                              : `${roundValue(toDecimal(c.capacityHours).minus(toDecimal(c.bookedHours)), 2)}h free`
                                        }
                                      />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </Collapse>
                      </TableCell>
                    </TableRow>,
                  ]
                })}
              </TableBody>
            </Table>

            {/* Project + task selectors */}
            <div className="row mt-3">
              <div className="col-md-3 col-4">
                <SelectInput
                  label="Project"
                  required={true}
                  placeholder="Project"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  items={projects.map((p) => ({ value: p.id, label: p.name }))}
                />
              </div>

              <div className="col-md-4 col-8">
                <MultiSelect
                  label="Task (optional)"
                  placeholder={tasksLoading ? 'Loading tasks…' : 'Select Task'}
                  items={taskOptions}
                  value={selectedTask}
                  onChangeHandler={(option) => setSelectedTask(option)}
                  isStage={true}
                  disabled={!projectId || tasksLoading}
                  isClearable={true}
                />
              </div>
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <button className="cancel-btn-white" onClick={onClose} type="button">
          Cancel
        </button>
        <button
          className="btnPrimary ms-2"
          disabled={!canSubmit}
          onClick={handleConfirm}
          type="button"
          style={!canSubmit ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          {submitting ? 'Saving…' : 'Confirm & Assign'}
        </button>
      </DialogActions>
    </Dialog>
  )
}

ConfirmAllocationsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  staff: PropTypes.array.isRequired,
  selectionByStaff: PropTypes.instanceOf(Map).isRequired,
  scheduleMap: PropTypes.instanceOf(Map).isRequired,
  availability: PropTypes.instanceOf(Map),
  projects: PropTypes.array.isRequired,
  onConfirm: PropTypes.func.isRequired,
}
